import * as React from 'react';
import {
    ShowBase,
    TextField,
    ReferenceField,
    ReferenceManyField,
    useShowContext,
} from 'react-admin';
import { 
    Box, 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    Chip,
    IconButton,
    Link,
    Divider,
    Alert
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    PlayArrow,
    Instagram,
    Twitter,
    Facebook,
    YouTube,
    MusicNote,
    CloudQueue,
    OpenInNew,
    Group,
    Radio,
    Star,
    Assessment,
} from '@mui/icons-material';

import { Avatar } from './Avatar';
import { ContactAside } from './ContactAside';
import { NotesIterator } from '../notes';
import { Contact } from '../types';
import { CompanyAvatar } from '../companies/CompanyAvatar';

import { useArtistMetrics, type ArtistMetrics } from '../hooks/useArtistMetrics';

import { useArtistTracks } from '../hooks/useArtistTracks';
import { TopTracksComponent, SonicProfileComponent } from '../components/TrackComponents';

import { useGEMSAnalysis } from '../hooks/useGEMSAnalysis';
import { 
    EmotionalIntelligenceComponent, 
    SonicSignatureComponent, 
    AudioFeaturesComponent 
} from '../components/GEMSComponents';

import { useArtistRegressionStatus } from '../hooks/useRegressionProspects';
import { 
    NextBigThingAlert,
    GrowthPredictionPanel, 
    ReferenceArtistComparisons,
    ContactStatusTracking 
} from '../components/RegressionArtistComponents';

import { supabase } from '../providers/supabase/supabase';

import { useArtistUrls } from '../hooks/useArtistUrls';

import { ArtistMomentumChart } from '../components/ArtistMomentumChart';

import { useArtistEmailData } from '../hooks/useArtistEmailData';
import { useEmailSender } from '../hooks/useEmailSender';
import EmailSequence from '../components/EmailSequence';
import { SonicRecommendations } from '../components/SonicRecommendations';


const formatNumber = (num?: number | null): string => {
    if (!num) return 'N/A';
    if (num >= 1000000) return `${(num/1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num/1000).toFixed(1)}K`;
    return num.toString();
};

const MetricCard = ({ title, value, icon, trend, color = 'primary' }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    trend?: 'up' | 'down' | 'stable';
    color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="caption" color="textSecondary">
                        {title}
                    </Typography>
                    <Typography variant="h6" component="div">
                        {value}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    {trend && (
                        <Box mr={1}>
                            {trend === 'up' && <TrendingUp color="success" fontSize="small" />}
                            {trend === 'down' && <TrendingDown color="error" fontSize="small" />}
                        </Box>
                    )}
                    <Box color={`${color}.main`}>
                        {icon}
                    </Box>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const SocialMediaLinks = ({ metrics, record, urls }: { metrics: ArtistMetrics; record?: Contact; urls?: any }) => {
    console.log('🎯 METRICS with URLs:', metrics);
    console.log('🔍 Available URLs:', {
        spotify: metrics.spotify_url,
        instagram: metrics.instagram_url,
        facebook: metrics.facebook_url,
        website: metrics.website_url
    });

    const platforms = [
        { 
            name: 'Spotify', 
            icon: <MusicNote />, 
            followers: metrics.spotify_followers, 
            color: '#1DB954',
            url: urls?.spotify_url
        },
        { 
            name: 'Instagram', 
            icon: <Instagram />, 
            followers: metrics.instagram_followers, 
            color: '#E4405F',
            url: metrics.instagram_url
        },
        { 
            name: 'YouTube', 
            icon: <YouTube />, 
            followers: metrics.youtube_subscribers, 
            color: '#FF0000',
            url: null
        },
        { 
            name: 'Twitter', 
            icon: <Twitter />, 
            followers: metrics.twitter_followers, 
            color: '#1DA1F2',
            url: null
        },
        { 
            name: 'Facebook', 
            icon: <Facebook />, 
            followers: metrics.facebook_followers, 
            color: '#4267B2',
            url: metrics.facebook_url
        },
    ];

    // Add website if it exists
    if (metrics.website_url) {
        platforms.push({
            name: 'Website',
            icon: <CloudQueue />,
            followers: null,
            color: '#666666',
            url: metrics.website_url
        });
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Social Media Presence
                </Typography>
                <Grid container spacing={2}>
                    {platforms.map((platform) => (
                        <Grid item xs={12} sm={6} md={4} key={platform.name}>
                            <Box 
                                display="flex" 
                                alignItems="center" 
                                p={1} 
                                border={1} 
                                borderColor="grey.200" 
                                borderRadius={1}
                            >
                                <Box color={platform.color} mr={1}>
                                    {platform.icon}
                                </Box>
                                <Box>
                                    <Typography variant="caption" display="block">
                                        {platform.name}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {platform.followers ? formatNumber(platform.followers) : 'Visit'}
                                    </Typography>
                                </Box>
                                <Box ml="auto">
                                    {platform.url ? (
                                        <IconButton 
                                            size="small"
                                            onClick={() => {
                                                console.log(`🔗 Opening ${platform.name}: ${platform.url}`);
                                                window.open(platform.url, '_blank');
                                            }}
                                            title={`Visit ${platform.name}`}
                                        >
                                            <OpenInNew fontSize="small" />
                                        </IconButton>
                                    ) : (
                                        <IconButton 
                                            size="small"
                                            disabled
                                            title="Link not available"
                                        >
                                            <OpenInNew fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
};

const SonicProfile = ({ metrics }: { metrics: ArtistMetrics }) => {
    const sonicFeatures = [
        { name: 'Valence', value: metrics.valence, description: 'Musical positivity' },
        { name: 'Energy', value: metrics.energy, description: 'Intensity & power' },
        { name: 'Danceability', value: metrics.danceability, description: 'Rhythmic drive' },
    ];

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Sonic Profile
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        {sonicFeatures.map((feature) => (
                            <Box key={feature.name} mb={2}>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="body2">{feature.name}</Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {feature.value ? Math.round(feature.value * 100) : 'N/A'}%
                                    </Typography>
                                </Box>
                                <Box 
                                    width="100%" 
                                    height={8} 
                                    bgcolor="grey.200" 
                                    borderRadius={4}
                                    position="relative"
                                >
                                    <Box
                                        width={`${(feature.value || 0) * 100}%`}
                                        height="100%"
                                        bgcolor="primary.main"
                                        borderRadius={4}
                                    />
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                    {feature.description}
                                </Typography>
                            </Box>
                        ))}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="body2" gutterBottom>
                                <strong>Tempo:</strong> {metrics.tempo || 'N/A'} BPM
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                <strong>Key:</strong> {metrics.key || 'N/A'} {metrics.mode || ''}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                <strong>Genre:</strong> {metrics.genre || 'N/A'}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const TrackInformation = ({ metrics }: { metrics: ArtistMetrics }) => (
    <Card>
        <CardContent>
            <Typography variant="h6" gutterBottom>
                Performance Overview
            </Typography>
            <Box display="flex" alignItems="center" mb={2}>
                <PlayArrow color="primary" sx={{ mr: 1 }} />
                <Box>
                    <Typography variant="body1" fontWeight="bold">
                        Spotify Performance
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {formatNumber(metrics.spotify_monthly_listeners)} monthly listeners
                    </Typography>
                </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Typography variant="body2">
                        Followers: <strong>{formatNumber(metrics.spotify_followers)}</strong>
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2">
                        Popularity: <strong>{metrics.spotify_popularity || 'N/A'}</strong>
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="body2">
                        Playlists: <strong>{formatNumber(metrics.spotify_playlist_count)}</strong>
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Chip 
                        label={metrics.career_stage || 'Unknown'} 
                        color="primary" 
                        size="small" 
                    />
                </Grid>
            </Grid>
        </CardContent>
    </Card>
);

export const ContactShow = () => (
    <ShowBase>
        <ContactShowContent />
    </ShowBase>
);

const ContactShowContent = () => {
    const { record, isPending } = useShowContext<Contact>();
    const { metrics: artistMetrics, loading: metricsLoading, error: metricsError } = useArtistMetrics(record?.artist_id);
    const { tracks, topTrack, sonicProfile, loading: tracksLoading, error: tracksError } = useArtistTracks(record?.artist_id);
    const { gemsData, topTrackGEMS, loading: gemsLoading, error: gemsError } = useGEMSAnalysis(record?.artist_id);
    const { regressionData, loading: regressionLoading, isRegressionProspect } = useArtistRegressionStatus(record?.artist_id);
    const { urls: artistUrls } = useArtistUrls(record?.artist_id);
    const { data: emailData, loading: emailLoading } = useArtistEmailData(record?.artist_id, record?.id);
    
    React.useEffect(() => {
        if (record?.artist_id) {
            console.log('✅ Found artist_id:', record.artist_id, 'for contact:', record.first_name, record.last_name);
        }
    }, [record?.artist_id, record?.first_name]);

    console.log('📈 METRICS DEBUG:');
    console.log('📈 Metrics Artist ID:', record?.artist_id);
    console.log('📈 Displayed Metrics:', artistMetrics);
    console.log('📈 Spotify Listeners (displayed):', artistMetrics?.spotify_monthly_listeners);
    console.log('📈 Instagram Followers (displayed):', artistMetrics?.instagram_followers);
    
    console.log('📧 EMAIL DATA DEBUG:');
    console.log('📧 Email Loading:', emailLoading);
    console.log('📧 Email Data:', emailData);
    console.log('📧 Email Data exists?', !!emailData);
    console.log('📧 Artist ID for email:', record?.artist_id);
    console.log('📧 Contact ID for email:', record?.id);
    console.log('📧 EMAIL SECTION CONDITION:', record?.artist_id && emailData);
    console.log('📧 HAS artist_id?', !!record?.artist_id);
    console.log('📧 HAS emailData?', !!emailData);
    
    if (isPending || !record) return null;

    return (
        <Box mt={2} mb={2} display="flex" gap={2}>
            <Box flex="1">
                {/* Original Contact Info */}
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Box display="flex">
                            <Avatar />
                            <Box ml={2} flex="1">
                                <Typography variant="h5">
                                    {record.first_name} {record.last_name}
                                </Typography>
                                <Typography variant="body2" component="div">
                                    {record.title}
                                    {record.title && record.company_id != null && ' at '}
                                    {record.company_id != null && (
                                        <ReferenceField
                                            source="company_id"
                                            reference="companies"
                                            link="show"
                                        >
                                            <TextField source="name" />
                                        </ReferenceField>
                                    )}
                                </Typography>
                            </Box>
                            <Box>
                                <ReferenceField
                                    source="company_id"
                                    reference="companies"
                                    link="show"
                                    sx={{ '& a': { textDecoration: 'none' } }}
                                >
                                    <CompanyAvatar />
                                </ReferenceField>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {console.log('🔍 BIO DEBUG:')}
                {console.log('🔍 record.artist_id:', record.artist_id)}
                {console.log('🔍 artistMetrics?.bio:', artistMetrics?.bio)}
                {console.log('🔍 artistMetrics?.artist_name:', artistMetrics?.artist_name)}

                {/* 🎯 ADD BIO CARD HERE */}
                    {record.artist_id && artistMetrics?.bio && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    About {artistMetrics.artist_name || record.first_name}
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                    {artistMetrics.bio}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                {/* Sonic Recommendations Section - Independent of artist metrics */}
                {record.artist_id && (
                    <Box sx={{ mb: 2 }}>
                        <SonicRecommendations artistId={record.artist_id} contactId={record.id} />
                    </Box>
                )}

                {/* Next Big Thing Alert - Show prominently if regression prospect */}
                {record.artist_id && isRegressionProspect && regressionData && (
                    <NextBigThingAlert regressionData={regressionData} />
                )}

                {/* Artist Analytics Section */}
                {record.artist_id && (
                    <>
                        {metricsLoading && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Loading artist analytics...
                            </Alert>
                        )}
                        
                        {metricsError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Error loading artist data: {metricsError}
                            </Alert>
                        )}
                        
                        {artistMetrics && !metricsLoading && (
                            <>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <strong>Artist Intelligence Dashboard</strong> - Latest metrics from Chartmetric (Updated: {artistMetrics.snapshot_date.toLocaleDateString()})
                                </Alert>

                        {/* Key Metrics Row */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <MetricCard
                                    title="Artist Score"
                                    value={artistMetrics.cm_artist_score ? Number(artistMetrics.cm_artist_score).toFixed(1) : 'N/A'}
                                    icon={<Star />}
                                    trend={artistMetrics.growth_trend || undefined}
                                    color="warning"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <MetricCard
                                    title="CM Rank"
                                    value={`#${formatNumber(artistMetrics.cm_artist_rank)}`}
                                    icon={<Assessment />}
                                    color="secondary"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <MetricCard
                                    title="Monthly Listeners"
                                    value={formatNumber(artistMetrics.spotify_monthly_listeners)}
                                    icon={<Radio />}
                                    trend={artistMetrics.growth_trend || undefined}
                                    color="success"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <MetricCard
                                    title="Total Followers"
                                    value={formatNumber(artistMetrics.total_social_followers)}
                                    icon={<Group />}
                                    trend={artistMetrics.growth_trend || undefined}
                                />
                            </Grid>
                        </Grid>

                        {/* 🎯 ADD THE MOMENTUM CHART HERE */}
                        <Box sx={{ mb: 2 }}>
                            <ArtistMomentumChart artistId={record.artist_id} />
                        </Box>

                        {/* Track Performance */}
                        <Box sx={{ mb: 2 }}>
                            <TrackInformation metrics={artistMetrics} />
                        </Box>

                        {/* Social Media Links */}
                        <Box sx={{ mb: 2 }}>
                            <SocialMediaLinks metrics={artistMetrics} record={record} urls={artistUrls}/>
                        </Box>

                        {/* Additional Platform Metrics */}
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Extended Platform Metrics
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>TikTok:</strong> {formatNumber(artistMetrics.tiktok_followers)} followers
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {formatNumber(artistMetrics.tiktok_likes)} likes • {artistMetrics.tiktok_engagement_rate ? `${(artistMetrics.tiktok_engagement_rate * 100).toFixed(1)}%` : 'N/A'} engagement
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>SoundCloud:</strong> {formatNumber(artistMetrics.soundcloud_followers)} followers
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {formatNumber(artistMetrics.soundcloud_plays)} plays
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>Shazam:</strong> {formatNumber(artistMetrics.shazam_count)} tags
                                        </Typography>
                                    </Grid>
                                    {artistMetrics.deezer_fans && (
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="body2" gutterBottom>
                                                <strong>Deezer:</strong> {formatNumber(artistMetrics.deezer_fans)} fans
                                            </Typography>
                                        </Grid>
                                    )}
                                    {artistMetrics.pandora_listeners && (
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="body2" gutterBottom>
                                                <strong>Pandora:</strong> {formatNumber(artistMetrics.pandora_listeners)} listeners (28d)
                                            </Typography>
                                        </Grid>
                                    )}
                                    {artistMetrics.genius_views && (
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="body2" gutterBottom>
                                                <strong>Genius:</strong> {formatNumber(artistMetrics.genius_views)} views
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                        {/* Top Tracks Section */}
                        <Box sx={{ mb: 2 }}>
                            <TopTracksComponent 
                                tracks={tracks} 
                                loading={tracksLoading} 
                            />
                        </Box>


                        {/* Sonic Profile Section */}
                        <Box sx={{ mb: 2 }}>
                            <SonicProfileComponent 
                                sonicProfile={sonicProfile} 
                                loading={tracksLoading} 
                            />
                        </Box>
                
                        {/* GEMS Error Handling */}
                        {record.artist_id && gemsError && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                GEMS Analysis unavailable: {gemsError}
                            </Alert>
                        )}

                        {/* Emotional Intelligence Section */}
                        {record.artist_id && (
                            <Box sx={{ mb: 2 }}>
                                <EmotionalIntelligenceComponent 
                                    gemsData={topTrackGEMS} 
                                    loading={gemsLoading} 
                                />
                            </Box>
                        )}

                        {/* Sonic Signature Section */}
                        {record.artist_id && (
                            <Box sx={{ mb: 2 }}>
                                <SonicSignatureComponent 
                                    gemsData={topTrackGEMS} 
                                    loading={gemsLoading} 
                                />
                            </Box>
                        )}

                        {/* Audio Features Section */}
                        {record.artist_id && (
                            <Box sx={{ mb: 2 }}>
                                <AudioFeaturesComponent 
                                    gemsData={topTrackGEMS} 
                                    loading={gemsLoading} 
                                />
                            </Box>
                        )}

                            </>
                        )}
                    </>
                )}

                {/* Regression Analysis - Only show for regression prospects */}
                {record.artist_id && isRegressionProspect && regressionData && (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <GrowthPredictionPanel regressionData={regressionData} />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <ReferenceArtistComparisons regressionData={regressionData} />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <ContactStatusTracking regressionData={regressionData} />
                        </Box>
                    </>
                )}

                {/* Original Notes Section */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Notes & Activity
                        </Typography>
                        <ReferenceManyField
                            target="contact_id"
                            reference="contactNotes"
                            sort={{ field: 'date', order: 'DESC' }}
                        >
                            <NotesIterator showStatus reference="contacts" />
                        </ReferenceManyField>
                    </CardContent>
                </Card>

                {/* Email Sequence Section */}
                {record.artist_id && emailData && (
                    <EmailSequence artistData={emailData} contactId={record.id} />
                )}
            </Box>
            <ContactAside />
        </Box>
    );
};